import { defineStore } from 'pinia'
import { ref } from 'vue'
import axios from '@/utils/apiClient'

export const useMenuStore = defineStore('menu', () => {
    const allowedRoutes = ref<Set<string>>(new Set())
    const rawMenus = ref<any[]>([])
    const isLoaded = ref(false)
    let fetchPromise: Promise<void> | null = null

    /**
     * Normalize a route path to lowercase with leading slash and without trailing slash.
     */
    function normalizePath(path: string): string {
        let p = path.trim().toLowerCase()
        if (!p.startsWith('/')) p = '/' + p
        if (p.length > 1 && p.endsWith('/')) p = p.slice(0, -1)
        return p
    }

    /**
     * Extract exact allowed routes from menu items returned by backend.
     */
    function setMenuItems(menus: any[]) {
        rawMenus.value = menus
        const routes = new Set<string>()

        const addPath = (routeStr: string) => {
            if (!routeStr) return
            const normalized = normalizePath(routeStr)
            routes.add(normalized)

            // If user route omits leading /user/ prefix, also record the /user/ version
            if (
                !normalized.startsWith('/user/') &&
                !normalized.startsWith('/frames/') &&
                !normalized.startsWith('/auth/')
            ) {
                routes.add(normalizePath('/user/' + normalized.slice(1)))
            }
        }

        const extract = (items: any[]) => {
            for (const item of items) {
                if (item.to) addPath(item.to)
                if (item.link) addPath(item.link)

                if (item.children && Array.isArray(item.children) && item.children.length > 0) {
                    extract(item.children)
                }
            }
        }

        extract(menus)
        allowedRoutes.value = routes
        isLoaded.value = true
    }

    /**
     * Fetch menu from API and populate allowed routes.
     * Caches the promise so multiple callers share a single HTTP request.
     */
    async function fetchAndSetMenu(): Promise<void> {
        if (isLoaded.value) return
        if (fetchPromise) return fetchPromise

        fetchPromise = (async () => {
            try {
                const userStr = localStorage.getItem('user')
                let isAdmin = false

                if (userStr) {
                    const user = JSON.parse(userStr)
                    const homepage = (user.homePage || '').toLowerCase().trim()
                    if (['/dashboard', 'superadmin', 'coo', 'hr department'].some(r => homepage.includes(r))) {
                        isAdmin = true
                    }
                }

                const res = await axios.get(`Auth/GetAccessMenu?IsAdmin=${isAdmin}`)
                const rawData = Array.isArray(res.data) ? res.data : []

                const seen = new Set<string>()
                const menus = [
                    {
                        title: 'Dashboard',
                        icon: 'ti ti-home',
                        chipColor: 'surface',
                        chipBgColor: 'secondary',
                        to: '/dashboard',
                    },
                    ...rawData,
                ].filter((item: any) => {
                    if (!item.to) return true
                    if (seen.has(item.to)) return false
                    seen.add(item.to)
                    return true
                })

                setMenuItems(menus)
            } catch (err) {
                console.error('Failed to fetch menu for route guard:', err)
                isLoaded.value = true
            } finally {
                fetchPromise = null
            }
        })()

        return fetchPromise
    }

    /**
     * Check if path is strictly allowed based on user menu permissions.
     */
    function isRouteAllowed(path: string): boolean {
        // Fail closed: if menu data is not loaded, deny access
        if (!isLoaded.value) return false

        const normalized = normalizePath(path)

        // 1. Exact route match
        if (allowedRoutes.value.has(normalized)) return true

        // 2. Dynamic parameter route match (e.g. /user/profile/123 -> matches parent /user/profile or /user/profile/:id)
        const parts = normalized.split('/')
        if (parts.length > 2) {
            const parentPath = parts.slice(0, -1).join('/')
            if (allowedRoutes.value.has(parentPath) || allowedRoutes.value.has(parentPath + '/:id')) return true
        }

        return false
    }

    function clearMenu() {
        allowedRoutes.value = new Set()
        rawMenus.value = []
        isLoaded.value = false
        fetchPromise = null
    }

    return { allowedRoutes, rawMenus, isLoaded, setMenuItems, fetchAndSetMenu, isRouteAllowed, clearMenu }
})
